import cx from 'clsx';
import { addMinutes, format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as ArrowDetailsIcon } from '@assets/svg/ic-tree-view-item.svg';
import { ReactComponent as ArrowIcon } from '@assets/svg/lightArrowDown.svg';

import Label, { type TVariant } from '@components/uikit/Label/Label';

import useTranslation from '@hooks/useTranslation';

import type { IAIReport } from '@model/aiReport';
import type { ICurrency } from '@model/currency';

import { useActionItemActiveReportQuery } from '@services/actionItem/useActionItemActiveReportQuery';
import { useActionItemInactiveReportsQuery } from '@services/actionItem/useActionItemInactiveReportsQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import type { IAiControls } from '../../model/IAiControls';
import ReportAction from '../ReportAction/ReportAction';
import ReportDetails from '../ReportDetails/ReportDetails';
import CompletionReportBlockSkeleton from './CompletionReportBlockSkeleton';
import style from './style.module.scss';

interface IProps {
  controls: IAiControls;
  userTimezone: number;
  expandedReport: IExpandedReport;
  currency: ICurrency;
  costSavingTemplateCurrency: ICurrency | null;
  setExpandedReport: React.Dispatch<React.SetStateAction<IExpandedReport>>;
}

type IExpandedReport = {
  [key: number]: boolean;
};

interface IReportState {
  text: string;
  variant: TVariant;
}

const countDisplayReports = 2;

const CompletionReportBlock: React.FC<IProps> = ({
  controls,
  userTimezone,
  expandedReport,
  currency,
  costSavingTemplateCurrency,
  setExpandedReport,
}) => {
  const { t } = useTranslation();
  const { actionItemId } = useParams();

  const [showAllReports, setShowAllReports] = useState(false);

  const { data: profileData } = useUserProfileQuery();

  const { data: activeReportData, isFetching: isActiveReportsFetching } = useActionItemActiveReportQuery({
    actionItemId: actionItemId || '',
  });

  const { data: inactiveReportsData, isFetching: isInactiveReportsFetching } =
    useActionItemInactiveReportsQuery({
      actionItemId: actionItemId || '',
    });

  const reports = useMemo(() => {
    let list: IAIReport[] = [];
    let qty: number = 0;

    if (activeReportData?.id) {
      list.push(activeReportData);
    }

    if (inactiveReportsData?.length) {
      list = [...list, ...inactiveReportsData];
    }

    qty = list.length;

    if (!showAllReports) {
      return { list: list.slice(0, countDisplayReports), qty };
    }

    return { list, qty };
  }, [activeReportData, inactiveReportsData, showAllReports]);

  useEffect(() => {
    if (reports.qty) {
      setExpandedReport({ [reports.list[0].id]: true });
    }
  }, [reports.qty]);

  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const isLoading = isActiveReportsFetching || isInactiveReportsFetching;

  if (isLoading) return <CompletionReportBlockSkeleton />;

  if (!reports.qty) {
    return null;
  }

  const reportStates: Record<number, IReportState> = {
    1: { text: 'Verifying', variant: 'high' },
    2: { text: 'Declined', variant: 'archived' },
    3: { text: 'Confirmed', variant: 'success' },
    4: { text: 'History', variant: 'archived' },
  };

  return (
    <div className={style.block}>
      <div className={style.blockHead}>
        <div className={style.blockTitle}>{t('Completion reports')}</div>
      </div>
      <div className={style.blockContainer}>
        <div className={style.tableWrapper}>
          <table className={style.reportsTable} cellSpacing="0" cellPadding="0">
            <thead className={style.reportsTableHead}>
              <tr>
                <th className={style.reportsTableTh}></th>
                <th className={style.reportsTableTh}>{t('Report date')}</th>
                <th className={style.reportsTableTh}>{t('Status')}</th>
                <th className={style.reportsTableTh}>{t('Submitted by')}</th>
                <th className={style.reportsTableTh}></th>
              </tr>
            </thead>
            <tbody className={style.reportsTableBody}>
              {reports.list.map((report) => (
                <React.Fragment key={uuidv4()}>
                  <tr className={style.reportsTableBodyRow}>
                    <td className={style.reportsTableTd}>
                      <ArrowDetailsIcon
                        className={cx(style.arrowDetailsIcon, {
                          [style.arrowTop]: !!expandedReport?.[report.id],
                        })}
                        onClick={() =>
                          setExpandedReport((state: IExpandedReport) => ({
                            ...state,
                            [report.id]: !expandedReport?.[report.id],
                          }))
                        }
                      />
                    </td>
                    <td className={style.reportsTableTd}>
                      {format(addMinutes(report?.created_at, userTimezone), dateTimeFormat)}
                    </td>
                    <td className={style.reportsTableTd}>
                      {report.state && (
                        <Label
                          text={t(reportStates[report.state].text)}
                          variant={reportStates[report.state].variant}
                        />
                      )}
                    </td>
                    <td className={style.reportsTableTd}>{report.verified_by}</td>
                    <td className={cx(style.reportsTableTd, style.cellAlignRight)}>
                      <ReportAction report={report} controls={controls} actionItemId={actionItemId} />
                    </td>
                  </tr>
                  <tr
                    className={cx(style.detailsRow, style.detailsRowActiveReport, {
                      [style.detailsRowIsOpen]: expandedReport?.[report.id],
                    })}
                  >
                    <td colSpan={5} className={style.reportsTableTdDetails}>
                      <ReportDetails
                        actionItemType={controls?.type}
                        isNoActions={controls?.isNoActions}
                        userTimezone={userTimezone}
                        report={report}
                        currency={currency}
                        costSavingTemplateCurrency={costSavingTemplateCurrency}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {reports.qty > countDisplayReports && (
          <div className={style.extraBlock}>
            <span onClick={() => setShowAllReports(!showAllReports)}>
              {showAllReports ? t('Collapse  history') : t('View all reports')}
              <ArrowIcon className={cx(style.arrowIcon, { [style.arrowTop]: showAllReports })} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletionReportBlock;
